import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { supabase } from '../lib/supabase';

// withCredentials global — nécessaire pour envoyer/recevoir les cookies HttpOnly
axios.defaults.withCredentials = true;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};


/** Messages d'erreur Supabase Auth en français (code renvoyé par Supabase). */
const authErrorMessage = (error) => {
  const code = error?.code || '';
  const msg = (error?.message || '').toLowerCase();
  if (code === 'invalid_credentials' || msg.includes('invalid login credentials')) {
    return 'Adresse e-mail ou mot de passe incorrect. Compte créé avec Google ? Utilisez la connexion depuis la plateforme Innovactive360°.';
  }
  if (code === 'email_not_confirmed' || msg.includes('email not confirmed')) return 'Votre adresse e-mail n\'est pas encore confirmée : ouvrez le lien reçu par e-mail.';
  if (code === 'captcha_failed' || msg.includes('captcha')) return 'La vérification de sécurité a échoué ou a expiré. Refaites-la puis réessayez.';
  if (code === 'over_request_rate_limit' || code === 'over_email_send_rate_limit' || error?.status === 429) return 'Trop de tentatives. Patientez quelques minutes.';
  if (code === 'weak_password' || msg.includes('password should')) return 'Mot de passe trop faible (8 caractères minimum, avec majuscule, minuscule et chiffre).';
  if (code === 'user_already_exists' || msg.includes('already registered')) return 'Un compte existe déjà avec cette adresse e-mail.';
  return 'Une erreur est survenue. Réessayez dans quelques instants.';
};

export const AuthProvider = ({ children }) => {
  const [user, setUser]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [authSource, setAuthSource] = useState(null);
  // token en mémoire React uniquement — jamais dans localStorage
  // Utilisé par les contextes enfants comme trigger de chargement
  const [token, setToken]           = useState(null);

  const buildUser = (supabaseSession, moocProfile) => {
    const su   = supabaseSession.user;
    const meta = su.user_metadata || {};
    return {
      id:          su.id,
      email:       su.email,
      firstName:   moocProfile?.firstName || meta.first_name || su.email.split('@')[0],
      lastName:    moocProfile?.lastName  || meta.last_name  || '',
      role:        moocProfile?.role      || 'user',
      provider:    su.app_metadata?.provider || 'google',
      avatar:      moocProfile?.avatar    || meta.avatar_url || null,
      phone:       moocProfile?.phone       || null,
      location:    moocProfile?.location    || null,
      bio:         moocProfile?.bio         || null,
      preferences: moocProfile?.preferences || {},
      createdAt:   moocProfile?.createdAt   || null,
      stats:       moocProfile?.stats       || {},
    };
  };

  // Échange le token Supabase contre un cookie HttpOnly côté backend
  const exchangeTokenForCookie = async (accessToken, refreshToken) => {
    try {
      await axios.post('/api/auth/session', {
        access_token:  accessToken,
        refresh_token: refreshToken || '',
      });
    } catch (e) {
      console.warn('[AuthContext] Échange cookie échoué, fallback Authorization header', e.message);
      // Fallback : header Authorization (le middleware accepte les deux)
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    }
  };

  // Supprime le header Authorization (on utilise le cookie comme auth principale)
  const clearAuthHeader = () => {
    delete axios.defaults.headers.common['Authorization'];
  };

  useEffect(() => {
    // Timer partagé : annulé par handleSsoMessage dès que le token arrive
    let ssoPendingTimer = null;
    // Guard anti-double setSession (evite deadlock Navigator LockManager)
    let ssoCompleted = false;

    // Listener postMessage SSO — installé avant initAuth pour ne manquer aucun envoi
    const handleSsoMessage = async (event) => {
      // Vérification d'origine stricte
      if (event.origin !== 'https://app.innovactive360.cloud') return;
      if (!event.data || event.data.type !== 'SSO_TOKEN' || !event.data.token) return;
      // Bloquer les retries si le SSO est déjà établi
      if (ssoCompleted) return;

      // Annuler le timer d'attente SSO (évite le setLoading(false) prématuré)
      if (ssoPendingTimer !== null) { clearTimeout(ssoPendingTimer); ssoPendingTimer = null; }

      const { token: ssoToken, refresh_token: refreshToken } = event.data;

      try {
        const { data, error } = await supabase.auth.setSession({
          access_token:  ssoToken,
          refresh_token: refreshToken || '',
        });

        if (error || !data.session) {
          // setSession échoué — afficher la page de login plutôt que rester bloqué
          setLoading(false);
          return;
        }

        await exchangeTokenForCookie(data.session.access_token, data.session.refresh_token);
        setToken(data.session.access_token);

        let moocProfile = null;
        try {
          const res = await axios.get('/api/auth/me');
          moocProfile = res.data?.user || null;
        } catch (e) { /* profil non bloquant */ }

        ssoCompleted = true;
        setUser(buildUser(data.session, moocProfile));
        setAuthSource('supabase');
        setLoading(false);
      } catch (e) {
        // Erreur inattendue — débloquer le chargement
        setLoading(false);
      }
    };

    window.addEventListener('message', handleSsoMessage);

    const initAuth = async () => {
      // PRIORITÉ 0 : SSO en attente — ouverture depuis la plateforme (flag ?sso=1 sans token)
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('sso') === '1') {
        window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
        // Handshake SSO_READY : signale à la plateforme que le module est prêt à recevoir le token.
        // La plateforme répond immédiatement au lieu d'attendre un délai fixe.
        if (window.opener && !window.opener.closed) {
          try {
            window.opener.postMessage({ type: 'SSO_READY' }, 'https://app.innovactive360.cloud');
          } catch { /* opener fermé ou cross-origin inattendu */ }
        }
        // Timeout étendu à 12s (couvre les chargements lents sur premier accès sans cache)
        ssoPendingTimer = setTimeout(() => setLoading(false), 12000);
        return; // Ne pas déclencher le finally — setLoading géré par le timer ou handleSsoMessage
      }

      try {
        // PRIORITÉ 1 : Session Supabase existante (stockée en sessionStorage)
        const { data: { session }, error } = await supabase.auth.getSession();

        if (session && !error) {
          await exchangeTokenForCookie(session.access_token, session.refresh_token);
          setToken(session.access_token);

          let moocProfile = null;
          try {
            const res = await axios.get('/api/auth/me');
            moocProfile = res.data?.user || null;
          } catch (e) { /* profil non bloquant */ }

          setUser(buildUser(session, moocProfile));
          setAuthSource('supabase');
          setLoading(false);
          return;
        }

        // PRIORITÉ 2 : Cookie de session existant (refresh de page sans session Supabase)
        try {
          const res = await axios.get('/api/auth/me');
          if (res.data?.user) {
            setUser(res.data.user);
            setAuthSource('cookie');
            // token non disponible depuis le cookie (HttpOnly) — c'est normal
            setLoading(false);
            return;
          }
        } catch (e) { /* pas de session cookie valide */ }

      } catch (error) {
        console.error('[AuthContext] Erreur initialisation auth:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Écouter les changements de session Supabase (refresh automatique du token)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        // SSO postMessage déjà traité — évite double fetch profil et double exchangeTokenForCookie
        if (ssoCompleted) return;
        await exchangeTokenForCookie(session.access_token, session.refresh_token);
        setToken(session.access_token);
        setAuthSource('supabase');
        axios.get('/api/auth/me')
          .then(res => setUser(buildUser(session, res.data?.user || null)))
          .catch(() => setUser(buildUser(session, null)));

      } else if (event === 'TOKEN_REFRESHED' && session) {
        await exchangeTokenForCookie(session.access_token, session.refresh_token);
        setToken(session.access_token);

      } else if (event === 'SIGNED_OUT') {
        clearAuthHeader();
        setUser(null);
        setToken(null);
        setAuthSource(null);
      }
    });

    return () => {
      clearTimeout(ssoPendingTimer);
      subscription?.unsubscribe();
      window.removeEventListener('message', handleSsoMessage);
    };
  }, []);

  const login = async (email, password, captchaToken) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: captchaToken ? { captchaToken } : undefined,
      });
      if (error) return { success: false, message: authErrorMessage(error) };

      await exchangeTokenForCookie(data.session.access_token, data.session.refresh_token);
      setToken(data.session.access_token);

      let moocProfile = null;
      try {
        const res = await axios.get('/api/auth/me');
        moocProfile = res.data?.user || null;
      } catch (e) { /* non bloquant */ }

      setUser(buildUser(data.session, moocProfile));
      setAuthSource('supabase');
      return { success: true };
    } catch (error) {
      return { success: false, message: 'Erreur de connexion' };
    }
  };

  const register = async (userData) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email:    userData.email,
        password: userData.password,
        options:  {
          data: {
            first_name: userData.firstName,
            last_name: userData.lastName,
            firstName: userData.firstName,
            lastName: userData.lastName,
            full_name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
          },
          captchaToken: userData.captchaToken || undefined,
        },
      });
      if (error) return { success: false, message: authErrorMessage(error) };

      // Adresse déjà inscrite : Supabase répond sans erreur mais sans identité
      if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
        return { success: false, message: 'Un compte existe déjà avec cette adresse e-mail. Connectez-vous (avec Google si le compte a été créé ainsi).' };
      }

      if (data.session) {
        await exchangeTokenForCookie(data.session.access_token, data.session.refresh_token);
        setToken(data.session.access_token);
        setUser(buildUser(data.session, null));
        setAuthSource('supabase');
      }
      return { success: true, needsConfirmation: !data.session };
    } catch (error) {
      return { success: false, message: 'Erreur d\'inscription' };
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const { data } = await axios.put('/api/auth/me', profileData);
      if (data.success) {
        const u = data.user;
        setUser(prev => ({
          ...prev,
          firstName:   u.first_name   !== undefined ? u.first_name   : prev.firstName,
          lastName:    u.last_name    !== undefined ? u.last_name    : prev.lastName,
          avatar:      u.avatar       !== undefined ? u.avatar       : prev.avatar,
          phone:       u.phone        !== undefined ? u.phone        : prev.phone,
          location:    u.location     !== undefined ? u.location     : prev.location,
          bio:         u.bio          !== undefined ? u.bio          : prev.bio,
          preferences: u.preferences  !== undefined ? u.preferences  : prev.preferences,
        }));
      }
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Erreur mise à jour profil' };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post('/api/auth/change-password', { currentPassword, newPassword });
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Erreur changement de mot de passe' };
    }
  };

  const logout = async () => {
    try {
      // 1. Révoquer la session côté backend (blacklist + clear cookie)
      await axios.post('/api/auth/logout');
    } catch (e) { /* continuer même si la requête échoue */ }

    // 2. Déconnecter de Supabase
    await supabase.auth.signOut();

    clearAuthHeader();
    setUser(null);
    setToken(null);
    setAuthSource(null);
  };

  const value = {
    user,
    token,        // En mémoire uniquement — jamais localStorage
    loading,
    authSource,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
